import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = ({ isCollapsed = false, isOpen = false, onClose }) => {
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState({});

  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'LayoutDashboard',
      tooltip: 'System overview and quick access'
    },
    {
      id: 'document-base',
      label: 'Document Bases',
      path: '/document-base-management',
      icon: 'Database',
      tooltip: 'Document management and storage'
    },
    {
      id: 'search-results',
      label: 'Search Results',
      path: '/search-results',
      icon: 'Search',
      tooltip: 'Search and find documents'
    },
    {
      id: 'ai-analysis',
      label: 'AI Analysis',
      path: '/ai-analysis-dashboard',
      icon: 'Zap',
      tooltip: 'AI-powered analysis and insights'
    },
    {
      id: 'ask-zentri',
      label: 'Ask Zentri',
      path: '/ask-zentri',
      icon: 'MessageSquare',
      tooltip: 'Natural language queries and assistance'
    },
    {
      id: 'workflow',
      label: 'Workflow',
      path: '/change-management',
      icon: 'GitBranch',
      tooltip: 'Change management and approvals'
    },
    {
      id: 'administration',
      label: 'Administration',
      icon: 'Settings',
      tooltip: 'User and system management',
      children: [
        {
          id: 'user-management',
          label: 'User Management',
          path: '/user-management',
          icon: 'Users'
        },
        {
          id: 'system-settings',
          label: 'System Settings',
          path: '/system-settings',
          icon: 'Cog'
        }
      ]
    }
  ];

  const isActiveRoute = (path) => {
    if (!path) return false;
    return location?.pathname === path;
  };

  const hasActiveChild = (children) => {
    if (!children) return false;
    return children?.some(child => isActiveRoute(child?.path));
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev?.[groupId]
    }));
  };

  const handleNavigation = (path) => {
    if (path) {
      window.location.href = path;
    }
    if (onClose) {
      onClose();
    }
  };

  const renderNavItem = (item, level = 0) => {
    const hasChildren = item?.children && item?.children?.length > 0;
    const isExpanded = expandedGroups?.[item?.id];
    const isActive = isActiveRoute(item?.path);
    const hasActiveChildren = hasActiveChild(item?.children);
    const shouldShowExpanded = isExpanded || hasActiveChildren;

    return (
      <div key={item?.id} className="w-full">
        <Button
          variant="ghost"
          onClick={() => {
            if (hasChildren) {
              toggleGroup(item?.id);
            } else {
              handleNavigation(item?.path);
            }
          }}
          className={`w-full justify-start gap-3 px-3 py-2.5 h-auto font-normal transition-all duration-200 ${
            level > 0 ? 'ml-6 pl-6' : ''
          } ${
            isActive 
              ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
              : hasActiveChildren
              ? 'bg-accent/10 text-accent hover:bg-accent/20' :'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
        >
          <Icon 
            name={item?.icon} 
            size={18} 
            className={`flex-shrink-0 ${
              isActive ? 'text-primary-foreground' : ''
            }`}
          />
          {!isCollapsed && (
            <>
              <span className="flex-1 text-left text-sm">{item?.label}</span>
              {hasChildren && (
                <Icon 
                  name="ChevronRight" 
                  size={16} 
                  className={`transition-transform duration-200 ${
                    shouldShowExpanded ? 'rotate-90' : ''
                  }`}
                />
              )}
            </>
          )}
        </Button>
        {hasChildren && !isCollapsed && shouldShowExpanded && (
          <div className="mt-1 space-y-1">
            {item?.children?.map(child => renderNavItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-100 lg:hidden"
          onClick={onClose}
        />
      )}
      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-16 bottom-0 bg-card border-r border-border z-100 transition-all duration-300 ease-out ${
          isCollapsed ? 'w-16' : 'w-60'
        } ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navigationItems?.map(item => renderNavItem(item))}
          </nav>

          {/* Collapse Toggle (Desktop Only) */}
          {!isCollapsed && (
            <div className="p-4 border-t border-border hidden lg:block">
              <div className="flex items-center gap-3 px-3 py-2">
                <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                  <Icon name="Building" size={16} className="text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    Enterprise Corp
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    Document Management
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;